import {
  Box,
  ButtonBase,
  Card,
  CardActionArea,
  Container,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Product } from "api";
import { useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Slider, { Settings as SliderSettings } from "react-slick";
import styled from "styled-components";
import theme from "theme";
import { setRefererInternal } from "modules/referer";

type CarouselProps = {
  products: Product[];
};

const Carousel = ({ products }: CarouselProps) => {
  const sliderRef = useRef<Slider>(null);

  const settings: SliderSettings = {
    arrows: false,
    className: "center",
    centerMode: true,
    centerPadding: "0",
    infinite: true,
    slidesToShow: 1,
    speed: 500,
    dots: true,
    autoplay: true,
    autoplaySpeed: 8000,
  };

  const moveForward = useCallback(() => {
    sliderRef.current?.slickNext();
  }, [sliderRef]);

  const moveBackward = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, [sliderRef]);

  return (
    <Box position="relative" mt="16px">
      <SliderContainer maxWidth="md">
        {products.length <= 1 ? (
          <div className="slick-center">
            {products.map((product) => {
              return (
                <CarouselItem
                  key={product.id}
                  productId={product.id}
                  {...product}
                />
              );
            })}
          </div>
        ) : (
          <Slider {...settings} ref={sliderRef}>
            {products.map((product) => {
              return (
                <CarouselItem
                  key={product.id}
                  productId={product.id}
                  {...product}
                />
              );
            })}
          </Slider>
        )}
      </SliderContainer>
      {products.length > 1 && (
        <>
          <ArrowButton align="right" onClick={moveForward}>
            <ArrowForwardIosIcon sx={{ color: "text.primary" }} />
          </ArrowButton>
          <ArrowButton align="left" onClick={moveBackward}>
            <ArrowBackIosNewIcon sx={{ color: "text.primary" }} />
          </ArrowButton>
        </>
      )}
    </Box>
  );
};

const SliderContainer = styled(Container)`
  .carousel-item {
    img {
      opacity: 0.3;
    }
  }
  .slick-center .carousel-item {
    img {
      opacity: 1;
    }
  }

  .item-description {
    opacity: 0;
  }

  .slick-center .item-description {
    opacity: 1;
  }

  .slick-list {
    overflow: visible;
  }

  .slick-dots {
    bottom: -30px;
  }
  .slick-dots li {
    width: auto;
    height: 8px;
    margin: 6px;
    ${theme.breakpoints.down("sm")} {
      margin: 4px;
    }
  }
  .slick-dots li button {
    width: 8px;
  }
  .slick-dots li button:before {
    content: "";
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 10px;
    opacity: 1;
    background-color: #606060;
  }

  .slick-dots li.slick-active button:before {
    opacity: 1;
    background-color: #f40057;
  }
`;

const ArrowButton = styled(ButtonBase)<{ align: "left" | "right" }>`
  background: #000;
  padding: 12px;
  border-radius: 0;
  position: absolute;
  top: calc(50% - 16px - 12px);
  ${({ align }) => align}: 0;

  &:hover {
    background: ${theme.palette.grey[800]};
  }
  ${theme.breakpoints.down("md")} {
    background: transparent;
    padding: 8px;
    top: calc(50% - 8px - 12px);
  }
`;

const ONE_WEEK_MSEC = 7 * 24 * 60 * 60 * 1000;
type CarouselItemProps = {
  productId: string;
  storeCarouselThumbnailUrl: string;
  category: string;
  name: string;
  publishedAt: string;
  description: string;
};

const CarouselItem = ({
  productId,
  storeCarouselThumbnailUrl,
  category,
  name,
  publishedAt,
  description,
}: CarouselItemProps) => {
  const onClickCard = useCallback(() => {
    setRefererInternal({ productId, referer: "store-top-page-main-visual" });
  }, [productId]);

  const isNew = useMemo(
    () => Date.parse(publishedAt) > Date.now() - ONE_WEEK_MSEC,
    [publishedAt]
  );

  return (
    <Box
      className="carousel-item"
      sx={{
        padding: "0 4px",
      }}
    >
      <Card sx={{ position: "relative", borderRadius: 0 }} elevation={0}>
        <CardActionArea
          component={Link}
          to={`/games/${productId}`}
          onClick={onClickCard}
        >
          <CardImage>
            <img src={storeCarouselThumbnailUrl} alt="" />
          </CardImage>
          {isNew && <NewLabel>NEW!!</NewLabel>}
          <Box
            className="item-description"
            sx={{
              position: "absolute",
              left: { sm: "24px", xs: "16px" },
              bottom: { sm: "24px", xs: "16px" },
              maxWidth: { md: "360px", xs: "calc(100% - 32px)" },
              display: { md: "grid" },
              alignItems: "end",
              gridAutoRows: "max-content",
              placeContent: "end",
              transition: "opacity 0.5s ease-out",
              color: theme.palette.text.primary,
            }}
          >
            <Typography
              fontWeight="bold"
              marginBottom="8px"
              sx={{
                width: "max-content",
                padding: "4px 5px",
                marginBottom: { md: "8px", xs: "4px" },
                fontSize: { md: "14px", xs: "11px" },
                lineHeight: "1",
                background: "#000",
              }}
            >
              {category}
            </Typography>
            <Typography
              fontWeight="bold"
              sx={{
                marginBottom: { md: "8px", xs: 0 },
                fontSize: { md: "24px", xs: "16px" },
                whiteSpace: { md: "unset", xs: "nowrap" },
                textOverflow: "ellipsis",
                overflow: "hidden",
                textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                lineHeight: "1.2",
              }}
            >
              {name}
            </Typography>

            <Typography
              fontSize="14px"
              color="#f5f5f5"
              sx={{
                display: { md: "block", xs: "none" },
                textShadow: "1px 1px 1px rgba(0,0,0,0.6)",
              }}
            >
              {description}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    </Box>
  );
};

const CardImage = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background-color: #000;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: opacity 0.5s ease-out;
  }
`;

const NewLabel = styled.div`
  position: absolute;
  top: 24px;
  font-family: sans-serif;
  left: 0;
  padding: 6px 12px 6px 24px;
  text-align: right;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  background-color: #f40057;
  ${theme.breakpoints.down("md")} {
    top: 20px;
    padding: 6px 12px 6px 20px;
  }
  ${theme.breakpoints.down("sm")} {
    top: 12px;
    left: 0;
    padding: 6px 8px 6px 12px;
    font-size: 14px;
  }
`;

export default Carousel;
